import { z } from 'zod'
import { geometryOutline, GEOMETRY_VERSION, type LocationGeometry, type PlanPoint } from './geometry'

/**
 * Plan dessiné d'un lieu (décision §9, option C) : murs, portes, fenêtres et passages, un niveau par étage.
 * Stocké sur le lieu (`locations.floor_plan`), indépendant des pièces : les pièces restent des rangements avec
 * leur forme (boîte ou polygone). Sans niveau dessiné pour un étage, les murs sont déduits des pièces
 * (`autoFloorPlan`). Unités : mètres, repère du lieu (comme `geometry.ts`).
 */

export const PlanWallSchema = z.object({
  id: z.string().min(1).max(40),
  x0: z.number().finite(),
  z0: z.number().finite(),
  x1: z.number().finite(),
  z1: z.number().finite(),
  thickness: z.number().positive().max(1).default(0.1),
  height: z.number().positive().max(20).default(2.5),
})
export type PlanWall = z.infer<typeof PlanWallSchema>

export const OPENING_KINDS = ['door', 'window', 'passage'] as const
export type OpeningKind = (typeof OPENING_KINDS)[number]

export const PlanOpeningSchema = z.object({
  id: z.string().min(1).max(40),
  wallId: z.string().min(1).max(40),
  kind: z.enum(OPENING_KINDS),
  /** Distance entre le début du mur (x0, z0) et le milieu de l'ouverture. */
  at: z.number().min(0).max(1000),
  width: z.number().positive().max(20),
  height: z.number().positive().max(20),
  /** Hauteur d'allège (bas de la fenêtre) ; 0 pour une porte ou un passage. */
  sill: z.number().min(0).max(20).default(0),
})
export type PlanOpening = z.infer<typeof PlanOpeningSchema>

/**
 * Escalier droit : axe de montée du bas (x0, z0) vers le haut (x1, z1), au sol de l'étage ; il monte jusqu'à
 * l'étage du dessus (hauteur calculée, voir `floorElevations`).
 */
export const PlanStairSchema = z.object({
  id: z.string().min(1).max(40),
  x0: z.number().finite(),
  z0: z.number().finite(),
  x1: z.number().finite(),
  z1: z.number().finite(),
  width: z.number().positive().max(5).default(0.9),
})
export type PlanStair = z.infer<typeof PlanStairSchema>

export const FloorPlanLevelSchema = z.object({
  floor: z.number().int().min(-5).max(50),
  /** Nom choisi (« Cave », « Grenier ») ; sinon nom par défaut d'après le numéro. */
  name: z.string().trim().min(1).max(60).optional(),
  /** Murs dessinés ; vide : murs déduits des pièces (le niveau peut n'exister que pour son nom ou ses escaliers). */
  walls: z.array(PlanWallSchema).max(1000),
  openings: z.array(PlanOpeningSchema).max(1000),
  stairs: z.array(PlanStairSchema).max(50).default([]),
})
export type FloorPlanLevel = z.infer<typeof FloorPlanLevelSchema>

export const FloorPlanSchema = z
  .object({
    version: z.literal(GEOMETRY_VERSION),
    kind: z.literal('floor_plan'),
    levels: z.array(FloorPlanLevelSchema).max(60),
  })
  .superRefine((plan, ctx) => {
    const floors = new Set<number>()
    plan.levels.forEach((level, i) => {
      if (floors.has(level.floor)) ctx.addIssue({ code: 'custom', path: ['levels', i, 'floor'], message: 'duplicate_floor' })
      floors.add(level.floor)
      const walls = new Set(level.walls.map((w) => w.id))
      level.openings.forEach((o, j) => {
        if (!walls.has(o.wallId)) {
          ctx.addIssue({ code: 'custom', path: ['levels', i, 'openings', j, 'wallId'], message: 'unknown_wall' })
        }
      })
    })
  })
export type FloorPlan = z.infer<typeof FloorPlanSchema>

export function parseFloorPlan(raw: unknown): FloorPlan | null {
  const parsed = FloorPlanSchema.safeParse(raw)
  return parsed.success ? parsed.data : null
}

export function planLevel(plan: FloorPlan | null | undefined, floor: number): FloorPlanLevel | null {
  return plan?.levels.find((l) => l.floor === floor) ?? null
}

/** Remplace (ou ajoute) le niveau d'un étage ; `null` le retire (l'étage revient aux murs déduits des pièces). */
export function withPlanLevel(plan: FloorPlan | null | undefined, floor: number, level: FloorPlanLevel | null): FloorPlan {
  const others = (plan?.levels ?? []).filter((l) => l.floor !== floor)
  return {
    version: GEOMETRY_VERSION,
    kind: 'floor_plan',
    levels: (level ? [...others, { ...level, floor }] : others).sort((a, b) => a.floor - b.floor),
  }
}

/** Tailles par défaut d'une ouverture posée sur un mur. */
export const OPENING_DEFAULTS: Record<OpeningKind, { width: number; height: number; sill: number }> = {
  door: { width: 0.9, height: 2.05, sill: 0 },
  window: { width: 1.2, height: 1.2, sill: 0.9 },
  passage: { width: 0.9, height: 2.1, sill: 0 },
}

// ---- Géométrie de base -------------------------------------------------------------------------

const r6 = (n: number) => Math.round(n * 1e6) / 1e6

export function wallLength(w: { x0: number; z0: number; x1: number; z1: number }): number {
  return Math.hypot(w.x1 - w.x0, w.z1 - w.z0)
}

/** Point du mur à la distance `t` de son début. */
export function pointOnWall(w: { x0: number; z0: number; x1: number; z1: number }, t: number): PlanPoint {
  const len = wallLength(w) || 1
  return { x: w.x0 + ((w.x1 - w.x0) * t) / len, z: w.z0 + ((w.z1 - w.z0) * t) / len }
}

/** Projection d'un point sur un mur : distance depuis le début (bornée au mur) et écart au mur. */
export function projectOnWall(w: { x0: number; z0: number; x1: number; z1: number }, p: PlanPoint) {
  const len = wallLength(w)
  if (len < 1e-9) return { at: 0, distance: Math.hypot(p.x - w.x0, p.z - w.z0) }
  const ux = (w.x1 - w.x0) / len
  const uz = (w.z1 - w.z0) / len
  const at = Math.min(len, Math.max(0, (p.x - w.x0) * ux + (p.z - w.z0) * uz))
  const q = { x: w.x0 + ux * at, z: w.z0 + uz * at }
  return { at, distance: Math.hypot(p.x - q.x, p.z - q.z) }
}

/** Ouverture ramenée dans son mur (au moins 10 cm de large, milieu borné pour qu'elle tienne entière). */
export function clampOpening(wall: PlanWall, o: PlanOpening): PlanOpening {
  const len = wallLength(wall)
  const width = Math.max(0.1, Math.min(o.width, len))
  const at = Math.min(len - width / 2, Math.max(width / 2, o.at))
  const height = Math.min(o.height, wall.height)
  const sill = o.kind === 'window' ? Math.min(o.sill, Math.max(0, wall.height - height)) : 0
  return { ...o, width: r6(width), at: r6(at), height: r6(height), sill: r6(sill) }
}

// ---- Rendu : morceaux de mur -------------------------------------------------------------------

/**
 * Morceau de mur à dessiner : segment au sol (x0,z0)→(x1,z1), de `y0` à `y1`.
 * `part` : mur plein, linteau (au-dessus d'une porte ou d'une fenêtre), allège (sous une fenêtre) ou vitre.
 */
export type WallPiece = {
  x0: number
  z0: number
  x1: number
  z1: number
  y0: number
  y1: number
  part: 'wall' | 'lintel' | 'sill' | 'glass'
  thickness: number
  /** Pièces que ce mur borde (murs déduits des pièces uniquement ; vide pour un plan dessiné). */
  roomIds: string[]
}

/** Mur avec, éventuellement, les pièces qu'il borde (murs déduits des pièces). */
export type LevelWall = PlanWall & { roomIds?: string[] }
export type LevelLike = { walls: LevelWall[]; openings: PlanOpening[]; stairs?: PlanStair[] }

/** Découpe chaque mur autour de ses ouvertures : morceaux pleins, linteaux, allèges et vitres. */
export function planWallPieces(level: LevelLike): WallPiece[] {
  const pieces: WallPiece[] = []
  for (const wall of level.walls) {
    const len = wallLength(wall)
    if (len < 0.01) continue
    const H = wall.height
    const roomIds = wall.roomIds ?? []
    const seg = (a: number, b: number, y0: number, y1: number, part: WallPiece['part']) => {
      if (b - a < 0.005 || y1 - y0 < 0.005) return
      const p = pointOnWall(wall, a)
      const q = pointOnWall(wall, b)
      pieces.push({ x0: r6(p.x), z0: r6(p.z), x1: r6(q.x), z1: r6(q.z), y0: r6(y0), y1: r6(y1), part, thickness: wall.thickness, roomIds })
    }
    const openings = level.openings
      .filter((o) => o.wallId === wall.id)
      .map((o) => clampOpening(wall, o))
      .sort((a, b) => a.at - b.at)
    let cursor = 0
    for (const o of openings) {
      // Ouvertures qui se chevauchent : la suivante commence où la précédente finit.
      const start = Math.max(cursor, o.at - o.width / 2)
      const end = o.at + o.width / 2
      if (end - start < 0.05) continue
      seg(cursor, start, 0, H, 'wall')
      const bottom = o.kind === 'window' ? o.sill : 0
      const top = Math.min(H, bottom + o.height)
      if (bottom > 0) seg(start, end, 0, bottom, 'sill')
      if (o.kind === 'window') seg(start, end, bottom, top, 'glass')
      seg(start, end, top, H, 'lintel')
      cursor = end
    }
    seg(cursor, len, 0, H, 'wall')
  }
  return pieces
}

// ---- Murs déduits des pièces -------------------------------------------------------------------

/** Largeur des portes ajoutées entre deux pièces voisines, et longueur commune minimale pour en mettre une. */
export const DOOR_WIDTH = 0.9
export const DOOR_MIN_SHARED = 1.1
export const DOOR_HEIGHT = 2.05
/** Deux bords à moins de 5 cm l'un de l'autre, parallèles, sont le même mur (pas de l'éditeur). */
const EDGE_TOLERANCE = 0.05

export type WallRoom = { id: string; geometry: LocationGeometry }

type Span = { lo: number; hi: number; height: number; roomIds: string[] }
/** Droite d'appui : direction unitaire `d`, normale `n`, décalage `c` (n · p = c pour tout point p). */
type Line = { dx: number; dz: number; c: number; spans: Span[] }

/**
 * Murs et portes déduits des pièces : chaque bord de pièce donne un mur, une cloison commune n'est posée qu'une
 * fois et reçoit une porte au milieu si elle mesure au moins `DOOR_MIN_SHARED` mètres. Fonctionne pour les
 * boîtes (tournées ou non) et les pièces de forme libre. Sert de point de départ au plan dessiné.
 */
export function autoFloorPlan(
  rooms: WallRoom[],
  floor = 0,
): { floor: number; walls: LevelWall[]; openings: PlanOpening[]; stairs: PlanStair[] } {
  const lines: Line[] = []
  for (const room of rooms) {
    const g = room.geometry
    const height = (g.y ?? 0) + g.height
    const pts = geometryOutline(g)
    pts.forEach((a, i) => {
      const b = pts[(i + 1) % pts.length]!
      const len = Math.hypot(b.x - a.x, b.z - a.z)
      if (len < 0.01) return
      let dx = (b.x - a.x) / len
      let dz = (b.z - a.z) / len
      // Direction canonique : deux bords opposés d'une même droite se retrouvent ensemble.
      if (dx < -1e-9 || (Math.abs(dx) <= 1e-9 && dz < 0)) {
        dx = -dx
        dz = -dz
      }
      const c = -dz * a.x + dx * a.z
      let line = lines.find((l) => Math.abs(l.dx * dz - l.dz * dx) < 1e-3 && Math.abs(-l.dz * a.x + l.dx * a.z - l.c) <= EDGE_TOLERANCE)
      if (!line) {
        line = { dx, dz, c, spans: [] }
        lines.push(line)
      }
      const ta = line.dx * a.x + line.dz * a.z
      const tb = line.dx * b.x + line.dz * b.z
      line.spans.push({ lo: Math.min(ta, tb), hi: Math.max(ta, tb), height, roomIds: [room.id] })
    })
  }

  const walls: LevelWall[] = []
  const openings: PlanOpening[] = []
  for (const line of lines) {
    // Découpe la droite aux extrémités de tous les bords : chaque tronçon sait quelles pièces il borde.
    const cuts = [...new Set(line.spans.flatMap((s) => [s.lo, s.hi]))].sort((a, b) => a - b)
    const parts: Span[] = []
    for (let i = 0; i + 1 < cuts.length; i++) {
      const lo = cuts[i]!
      const hi = cuts[i + 1]!
      if (hi - lo < 0.01) continue
      const over = line.spans.filter((s) => s.lo <= lo + 1e-6 && s.hi >= hi - 1e-6)
      if (!over.length) continue
      const roomIds = [...new Set(over.flatMap((s) => s.roomIds))]
      const height = Math.max(...over.map((s) => s.height))
      const prev = parts[parts.length - 1]
      // Tronçons voisins bordant les mêmes pièces : un seul mur.
      if (prev && Math.abs(prev.hi - lo) < 1e-6 && prev.roomIds.join() === roomIds.join() && prev.height === height) prev.hi = hi
      else parts.push({ lo, hi, height, roomIds })
    }
    for (const p of parts) {
      // Point de la droite : t le long de d, décalage c le long de n = (−dz, dx).
      const at = (t: number) => ({ x: r6(t * line.dx - line.c * line.dz), z: r6(t * line.dz + line.c * line.dx) })
      const a = at(p.lo)
      const b = at(p.hi)
      const id = `auto-w${walls.length + 1}`
      walls.push({ id, x0: a.x, z0: a.z, x1: b.x, z1: b.z, thickness: 0.1, height: p.height, roomIds: p.roomIds })
      if (p.roomIds.length >= 2 && p.hi - p.lo >= DOOR_MIN_SHARED) {
        openings.push({
          id: `auto-o${openings.length + 1}`,
          wallId: id,
          kind: 'door',
          at: r6((p.hi - p.lo) / 2),
          width: DOOR_WIDTH,
          height: r6(Math.min(DOOR_HEIGHT, p.height - 0.2)),
          sill: 0,
        })
      }
    }
  }
  return { floor, walls, openings, stairs: [] }
}

/** Murs d'un étage déduits des pièces, prêts à dessiner (voir `autoFloorPlan`). */
export function roomWalls(rooms: WallRoom[]): WallPiece[] {
  return planWallPieces(autoFloorPlan(rooms))
}

/** Niveau enregistrable (sans les pièces bordées, propres aux murs déduits). */
export function toPlanLevel(level: {
  floor: number
  name?: string
  walls: LevelWall[]
  openings: PlanOpening[]
  stairs?: PlanStair[]
}): FloorPlanLevel {
  return {
    floor: level.floor,
    ...(level.name ? { name: level.name } : {}),
    walls: level.walls.map(({ roomIds: _roomIds, ...w }) => w),
    openings: level.openings,
    stairs: level.stairs ?? [],
  }
}

// ---- Étages : hauteurs et escaliers ---------------------------------------------------------------

/** Hauteur d'un étage sans mur ni pièce, et épaisseur du plancher entre deux étages. */
export const DEFAULT_FLOOR_HEIGHT = 2.5
export const SLAB_THICKNESS = 0.2

/** Hauteur d'un étage : son mur ou sa pièce le plus haut, sinon 2,5 m. */
export function levelHeight(level: { walls: { height: number }[] } | null | undefined, rooms: LocationGeometry[] = []): number {
  const hs = [...(level?.walls ?? []).map((w) => w.height), ...rooms.map((g) => (g.y ?? 0) + g.height)]
  return hs.length ? Math.max(...hs) : DEFAULT_FLOOR_HEIGHT
}

/**
 * Altitude du sol de chaque étage (rez-de-chaussée = 0) : on empile les hauteurs d'étage et un plancher de
 * `SLAB_THICKNESS` entre deux ; un étage sans hauteur connue (sauté, vide) compte 2,5 m.
 */
export function floorElevations(heights: Map<number, number>, floors: number[]): Map<number, number> {
  const out = new Map<number, number>()
  if (!floors.length) return out
  const h = (f: number) => heights.get(f) ?? DEFAULT_FLOOR_HEIGHT
  const min = Math.min(...floors, 0)
  const max = Math.max(...floors, 0)
  let y = 0
  for (let f = 0; f <= max; f++) {
    out.set(f, r6(y))
    y += h(f) + SLAB_THICKNESS
  }
  y = 0
  for (let f = -1; f >= min; f--) {
    y -= h(f) + SLAB_THICKNESS
    out.set(f, r6(y))
  }
  return new Map(floors.map((f) => [f, out.get(f)!]))
}

/** Marches d'un escalier droit qui monte de `rise` mètres : nombre (environ 18 cm chacune), hauteur, giron. */
export function stairSteps(stair: { x0: number; z0: number; x1: number; z1: number }, rise: number) {
  const length = Math.hypot(stair.x1 - stair.x0, stair.z1 - stair.z0)
  const count = Math.max(2, Math.round(rise / 0.18))
  return { length, count, riser: rise / count, tread: length / count }
}

/** Contour au sol d'un escalier (quatre coins, du bas à gauche au bas à droite). */
export function stairOutline(s: PlanStair): PlanPoint[] {
  const len = Math.hypot(s.x1 - s.x0, s.z1 - s.z0) || 1
  const nx = (-(s.z1 - s.z0) / len) * (s.width / 2)
  const nz = ((s.x1 - s.x0) / len) * (s.width / 2)
  return [
    { x: r6(s.x0 + nx), z: r6(s.z0 + nz) },
    { x: r6(s.x1 + nx), z: r6(s.z1 + nz) },
    { x: r6(s.x1 - nx), z: r6(s.z1 - nz) },
    { x: r6(s.x0 - nx), z: r6(s.z0 - nz) },
  ]
}

/** Ajoute un escalier (au moins 1 m de long). */
export function addStair<L extends LevelLike>(level: L, stair: PlanStair): L {
  if (Math.hypot(stair.x1 - stair.x0, stair.z1 - stair.z0) < 1) return level
  return { ...level, stairs: [...(level.stairs ?? []), stair] }
}

export function updateStair<L extends LevelLike>(level: L, id: string, patch: Partial<Omit<PlanStair, 'id'>>): L {
  return { ...level, stairs: (level.stairs ?? []).map((s) => (s.id === id ? { ...s, ...patch } : s)) }
}

export function moveStair<L extends LevelLike>(level: L, id: string, dx: number, dz: number): L {
  return {
    ...level,
    stairs: (level.stairs ?? []).map((s) =>
      s.id === id ? { ...s, x0: r6(s.x0 + dx), z0: r6(s.z0 + dz), x1: r6(s.x1 + dx), z1: r6(s.z1 + dz) } : s,
    ),
  }
}

export function removeStair<L extends LevelLike>(level: L, id: string): L {
  return { ...level, stairs: (level.stairs ?? []).filter((s) => s.id !== id) }
}

// ---- Outils de l'éditeur -----------------------------------------------------------------------

export const snapTo = (n: number, step: number) => r6(Math.round(n / step) * step)

/**
 * Aimantation d'un point : sur une cible proche (extrémité de mur, coin de pièce) si elle est à moins de `radius`,
 * sinon sur la grille `step`. Avec `from`, l'angle est aussi aimanté par pas de 45° (à `angleTolDeg` près).
 */
export function snapPoint(
  p: PlanPoint,
  opts: { targets?: PlanPoint[]; radius?: number; step?: number; from?: PlanPoint | null; angleTolDeg?: number },
): PlanPoint {
  const radius = opts.radius ?? 0.2
  let best: PlanPoint | null = null
  let bestD = radius
  for (const t of opts.targets ?? []) {
    const d = Math.hypot(t.x - p.x, t.z - p.z)
    if (d <= bestD) {
      best = t
      bestD = d
    }
  }
  if (best) return { x: best.x, z: best.z }
  const step = opts.step ?? 0.05
  let q = p
  if (opts.from) {
    const dx = p.x - opts.from.x
    const dz = p.z - opts.from.z
    const len = Math.hypot(dx, dz)
    if (len > 1e-6) {
      const angle = Math.atan2(dz, dx)
      const snapped = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4)
      if (Math.abs(angle - snapped) <= ((opts.angleTolDeg ?? 6) * Math.PI) / 180) {
        const l = snapTo(len, step)
        return { x: r6(opts.from.x + Math.cos(snapped) * l), z: r6(opts.from.z + Math.sin(snapped) * l) }
      }
    }
  }
  q = { x: snapTo(q.x, step), z: snapTo(q.z, step) }
  return q
}

const samePoint = (a: PlanPoint, b: PlanPoint, tol = 0.01) => Math.hypot(a.x - b.x, a.z - b.z) <= tol

/** Extrémités de murs (sans doublons), cibles de l'aimantation. */
export function wallEndpoints(level: LevelLike): PlanPoint[] {
  const out: PlanPoint[] = []
  for (const w of level.walls) {
    for (const p of [
      { x: w.x0, z: w.z0 },
      { x: w.x1, z: w.z1 },
    ]) {
      if (!out.some((q) => samePoint(p, q))) out.push(p)
    }
  }
  return out
}

/** Les ouvertures restent dans leur mur après un changement de longueur. */
function refit<L extends LevelLike>(level: L): L {
  const byId = new Map(level.walls.map((w) => [w.id, w]))
  return {
    ...level,
    openings: level.openings.flatMap((o) => {
      const w = byId.get(o.wallId)
      return w && wallLength(w) >= 0.1 ? [clampOpening(w, o)] : []
    }),
  }
}

/** Déplace un angle : toutes les extrémités de murs posées en `from` vont en `to` (les murs restent joints). */
export function moveJoint<L extends LevelLike>(level: L, from: PlanPoint, to: PlanPoint): L {
  return refit({
    ...level,
    walls: level.walls.map((w) => ({
      ...w,
      ...(samePoint({ x: w.x0, z: w.z0 }, from) ? { x0: to.x, z0: to.z } : {}),
      ...(samePoint({ x: w.x1, z: w.z1 }, from) ? { x1: to.x, z1: to.z } : {}),
    })),
  })
}

/** Déplace un mur entier ; les murs qui le touchent suivent par leur extrémité commune. */
export function moveWall<L extends LevelLike>(level: L, wallId: string, dx: number, dz: number): L {
  const w = level.walls.find((x) => x.id === wallId)
  if (!w) return level
  const a = { x: w.x0, z: w.z0 }
  const b = { x: w.x1, z: w.z1 }
  const a2 = { x: r6(a.x + dx), z: r6(a.z + dz) }
  const b2 = { x: r6(b.x + dx), z: r6(b.z + dz) }
  return refit({
    ...level,
    walls: level.walls.map((x) => {
      const out = { ...x }
      if (samePoint({ x: x.x0, z: x.z0 }, a)) Object.assign(out, { x0: a2.x, z0: a2.z })
      else if (samePoint({ x: x.x0, z: x.z0 }, b)) Object.assign(out, { x0: b2.x, z0: b2.z })
      if (samePoint({ x: x.x1, z: x.z1 }, a)) Object.assign(out, { x1: a2.x, z1: a2.z })
      else if (samePoint({ x: x.x1, z: x.z1 }, b)) Object.assign(out, { x1: b2.x, z1: b2.z })
      return out
    }),
  })
}

/** Ajoute un mur (ignoré s'il fait moins de 5 cm). */
export function addWall<L extends LevelLike>(level: L, wall: PlanWall): L {
  if (wallLength(wall) < 0.05) return level
  return { ...level, walls: [...level.walls, wall] }
}

/** Retire un mur et ses ouvertures. */
export function removeWall<L extends LevelLike>(level: L, wallId: string): L {
  return { ...level, walls: level.walls.filter((w) => w.id !== wallId), openings: level.openings.filter((o) => o.wallId !== wallId) }
}

/** Pose une ouverture sur un mur, centrée en `at` (bornée au mur), aux tailles par défaut de son type. */
export function addOpening<L extends LevelLike>(level: L, input: { id: string; wallId: string; kind: OpeningKind; at: number }): L {
  const wall = level.walls.find((w) => w.id === input.wallId)
  if (!wall || wallLength(wall) < 0.2) return level
  const o = clampOpening(wall, { id: input.id, wallId: wall.id, kind: input.kind, at: input.at, ...OPENING_DEFAULTS[input.kind] })
  return { ...level, openings: [...level.openings, o] }
}

/** Modifie une ouverture (taille, position, type), toujours ramenée dans son mur. */
export function updateOpening<L extends LevelLike>(level: L, id: string, patch: Partial<Omit<PlanOpening, 'id' | 'wallId'>>): L {
  const byId = new Map(level.walls.map((w) => [w.id, w]))
  return {
    ...level,
    openings: level.openings.map((o) => {
      if (o.id !== id) return o
      const wall = byId.get(o.wallId)
      const next = { ...o, ...patch }
      if (patch.kind && patch.kind !== o.kind) Object.assign(next, OPENING_DEFAULTS[patch.kind], patch)
      return wall ? clampOpening(wall, next) : next
    }),
  }
}

export function removeOpening<L extends LevelLike>(level: L, id: string): L {
  return { ...level, openings: level.openings.filter((o) => o.id !== id) }
}

/** Modifie un mur (épaisseur, hauteur, extrémités) ; ses ouvertures restent dedans. */
export function updateWall<L extends LevelLike>(level: L, id: string, patch: Partial<Omit<PlanWall, 'id'>>): L {
  return refit({ ...level, walls: level.walls.map((w) => (w.id === id ? { ...w, ...patch } : w)) })
}

/** Ajoute un point au contour d'une pièce, après le point `index`. */
export function insertPoint(points: PlanPoint[], index: number, p: PlanPoint): PlanPoint[] {
  return [...points.slice(0, index + 1), p, ...points.slice(index + 1)]
}

/** Retire un point du contour (au moins trois restent). */
export function removePoint(points: PlanPoint[], index: number): PlanPoint[] {
  return points.length <= 3 ? points : points.filter((_, i) => i !== index)
}
